import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function PremiumWines() {
  const [selectedWines, setSelectedWines] = useState<string[]>([]);
  const navigate = useNavigate();

  const wines = [
    'Tall Horse Cabernet Sauvignon',
    'Tall Horse Shiraz',
    '4th Street Red',
    'Four Cousins Natural Sweet Red',
    'Château Asconi Red',
    '19 Crimes Red Blend',
    'Tall Horse Chenin Blanc',
    'Four Cousins White',
    '2Share White Wine',
    'Lamothe Parrot Classic White',
    'Mosketto White',
    'SSR Smooth Rosé',
    'Mosketto Rosé',
    'Mosketto Red',
    'Mosketto Sweet Wine'
  ];

  const handleCheckboxChange = (wine: string) => {
    setSelectedWines(prev => 
      prev.includes(wine) 
        ? prev.filter(w => w !== wine)
        : [...prev, wine]
    );
  };

  const handleSubmit = () => {
    if (selectedWines.length === 0) {
      alert('Please select at least one wine');
      return;
    }
    
    // Get existing items from localStorage
    const existingItems = localStorage.getItem('selectedOrderItems');
    let allItems: string[] = [];
    
    if (existingItems) {
      try {
        allItems = JSON.parse(existingItems);
      } catch {
        allItems = [];
      }
    }
    
    // Add new selections (avoid duplicates)
    selectedWines.forEach(wine => {
      if (!allItems.includes(wine)) {
        allItems.push(wine);
      }
    });
    
    // Save to localStorage
    localStorage.setItem('selectedOrderItems', JSON.stringify(allItems));
    
    // Dispatch custom event to update header count
    window.dispatchEvent(new Event('orderItemsUpdated'));
    
    // Show success message
    const message = document.createElement('div');
    message.className = 'fixed top-24 right-4 bg-green-600 text-white px-6 py-4 rounded-lg shadow-xl z-50 animate-fade-in';
    message.innerHTML = `<i class="ri-check-line mr-2"></i>${selectedWines.length} item(s) added to your order`;
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.remove();
    }, 3000);
    
    // Reset selections
    setSelectedWines([]);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-black mb-4">
                Premium <span className="text-yellow-600">Wines</span>
              </h1>
              <p className="text-xl text-gray-600">
                Select from our collection of top premium wine brands available in Ghana
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  RED WINE
                </h2>
                <div className="space-y-4">
                  {wines.slice(0, 6).map((wine, index) => (
                    <label 
                      key={index}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedWines.includes(wine)}
                        onChange={() => handleCheckboxChange(wine)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {wine}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  WHITE WINES
                </h2>
                <div className="space-y-4">
                  {wines.slice(6, 11).map((wine, index) => (
                    <label 
                      key={index + 6}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedWines.includes(wine)}
                        onChange={() => handleCheckboxChange(wine)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {wine}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  ROSÉ WINES
                </h2>
                <div className="space-y-4">
                  {wines.slice(11, 13).map((wine, index) => (
                    <label 
                      key={index + 11}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedWines.includes(wine)}
                        onChange={() => handleCheckboxChange(wine)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {wine}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-black mb-6 pb-3 border-b-2 border-yellow-600">
                  SPARKLING/SWEET WINES
                </h2>
                <div className="space-y-4">
                  {wines.slice(13).map((wine, index) => (
                    <label 
                      key={index + 13}
                      className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        checked={selectedWines.includes(wine)}
                        onChange={() => handleCheckboxChange(wine)}
                        className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-4 text-lg text-gray-800 group-hover:text-yellow-600 transition-colors duration-200">
                        {wine}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  variant="primary" 
                  size="lg"
                  onClick={handleSubmit}
                >
                  <i className="ri-check-line mr-2"></i>
                  Submit Selection
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
